
import { useInfiniteQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { getValidToken } from "@/utils/gmail/tokenUtils";
import { fetchGmailEmails } from "@/utils/gmail/fetchUtils";

interface Email {
  id: string;
  subject: string;
  sender: string;
  preview: string;
  date: string;
  content?: string;
  htmlContent?: string;
}

interface EmailResponse {
  emails: Email[];
  nextPageToken: string | null;
}

export const useGmailFetch = (selectedInbox?: string) => {
  const { toast } = useToast();
  const PAGE_SIZE = 20;

  const fetchImapEmails = async (account: any, pageSize: number) => {
    try {
      const { data, error } = await supabase.functions.invoke('email-operations', {
        body: {
          action: 'fetch',
          protocol: account.provider,
          user: account.email,
          password: account.credentials.password,
          // IMAP specific params
          ...(account.provider === 'imap' && {
            imapHost: account.credentials.imap.host,
            imapPort: account.credentials.imap.port,
            smtpHost: account.credentials.smtp.host,
            smtpPort: account.credentials.smtp.port,
            tls: account.credentials.imap.tls,
          }),
          // Exchange specific params
          ...(account.provider === 'exchange' && {
            exchangeUrl: account.credentials.exchange.url,
            domain: account.credentials.exchange.domain,
            authType: account.credentials.exchange.authType,
          }),
          limit: pageSize
        }
      });

      if (error) {
        throw error;
      }
      
      if (!data || !data.success) {
        throw new Error('Failed to fetch emails');
      }

      const emails = data.data.map((message: any) => ({
        id: message.id.toString(),
        subject: message.headers.subject || "No Subject",
        sender: message.headers.from || "Unknown Sender",
        preview: message.content.substring(0, 100),
        date: new Date(message.headers.date).toLocaleDateString(),
        content: message.content,
        htmlContent: message.content.includes('<html') ? message.content : null
      }));

      return {
        emails,
        nextPageToken: null
      };
    } catch (error) {
      console.error("Error in fetchImapEmails:", error);
      throw error;
    }
  };

  return useInfiniteQuery({
    queryKey: ['emails', selectedInbox],
    queryFn: async ({ pageParam = null }) => {
      if (!selectedInbox) {
        return { emails: [], nextPageToken: null };
      }

      try {
        // Get the account details for the selected inbox
        const { data: accounts, error: accountError } = await supabase
          .from('email_accounts')
          .select('*')
          .eq('email', selectedInbox)
          .maybeSingle();

        if (accountError) {
          throw new Error('Email account not found');
        }

        if (!accounts) {
          throw new Error('Email account not found');
        }

        // For Gmail accounts
        if (accounts.provider === 'gmail') {
          const token = getValidToken(selectedInbox);
          if (!token) {
            // Clear the account from the database if token is invalid
            await supabase
              .from('email_accounts')
              .delete()
              .eq('email', selectedInbox);
              
            throw new Error('Invalid or expired Gmail token');
          }
          return fetchGmailEmails(token, PAGE_SIZE, pageParam as string | null);
        }

        // For IMAP and Exchange accounts
        return fetchImapEmails(accounts, PAGE_SIZE);
      } catch (error: any) {
        console.error("Error fetching emails:", error);
        throw error;
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
    initialPageParam: null as string | null,
    enabled: !!selectedInbox,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    retry: 1,
    meta: {
      onError: (error: Error) => {
        toast({
          title: "Error",
          description: "Failed to fetch emails. Please try reconnecting your email account.",
          variant: "destructive",
        });
      }
    }
  });
};
